import React, { Component, ReactNode } from 'react';
import { Result, Button, Card, Typography } from 'antd';
import { ReloadOutlined, HomeOutlined } from '@ant-design/icons';

const { Paragraph, Text } = Typography;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: any;
}

class AuditErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true, 
      error 
    };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // Log error to monitoring service
    console.error('Audit Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Report to error tracking service (Sentry, LogRocket, etc.)
    // errorTrackingService.captureException(error, {
    //   extra: errorInfo,
    //   tags: {
    //     component: 'AuditErrorBoundary'
    //   }
    // });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    this.props.onReset?.();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div style={{ 
          minHeight: '60vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '24px'
        }}>
          <Card style={{ maxWidth: 600, width: '100%' }}>
            <Result
              status="error"
              title="Có lỗi xảy ra với tính năng kiểm kê"
              subTitle="Xin lỗi, đã có lỗi không mong muốn xảy ra. Vui lòng thử lại hoặc liên hệ quản trị viên."
              extra={[
                <Button 
                  key="retry" 
                  type="primary" 
                  icon={<ReloadOutlined />}
                  onClick={this.handleReset}
                >
                  Thử lại
                </Button>,
                <Button 
                  key="home" 
                  icon={<HomeOutlined />}
                  onClick={this.handleGoHome}
                >
                  Về trang chủ
                </Button>
              ]}
            >
              {/* Error details for development */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div style={{ marginTop: 24, textAlign: 'left' }}>
                  <Typography.Title level={5} type="danger">
                    Chi tiết lỗi (Development):
                  </Typography.Title>
                  
                  <Card size="small" style={{ backgroundColor: '#fff2f0' }}>
                    <Paragraph>
                      <Text strong>Error:</Text> {this.state.error.message}
                    </Paragraph>
                    
                    <Paragraph>
                      <Text strong>Stack:</Text>
                      <pre style={{ 
                        fontSize: '12px', 
                        backgroundColor: '#f5f5f5', 
                        padding: '8px',
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '200px'
                      }}>
                        {this.state.error.stack}
                      </pre>
                    </Paragraph>

                    {this.state.errorInfo?.componentStack && (
                      <Paragraph>
                        <Text strong>Component Stack:</Text>
                        <pre style={{ 
                          fontSize: '12px', 
                          backgroundColor: '#f5f5f5', 
                          padding: '8px',
                          borderRadius: '4px',
                          overflow: 'auto',
                          maxHeight: '200px'
                        }}>
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </Paragraph>
                    )}
                  </Card>
                </div>
              )}
            </Result>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuditErrorBoundary;

// HOC wrapper for easier usage
export const withAuditErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) => {
  const WrappedComponent = (props: P) => (
    <AuditErrorBoundary fallback={fallback}>
      <Component {...props} />
    </AuditErrorBoundary>
  );

  WrappedComponent.displayName = `withAuditErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

// Hook for error reporting
export const useErrorHandler = () => {
  const handleError = React.useCallback((error: Error, errorInfo?: any) => {
    console.error('Manual error report:', error, errorInfo);
    
    // Report to error tracking service
    // errorTrackingService.captureException(error, {
    //   extra: errorInfo,
    //   tags: {
    //     component: 'useErrorHandler'
    //   }
    // });
  }, []);

  return { handleError };
}; 